import { Controller } from "@hotwired/stimulus"

// Manages the alternate-locations picker in the edit/add drawers.
// Supports add (from server-rendered search results), remove, and
// move-up/move-down. Maintains an ordered list of hidden inputs named
// `partner_provider_setting[alternate_locations][]`.
//
// Search is handled by the `auto-search` Stimulus controller mounted on the
// search input — it fires a Turbo Stream request and the server renders
// results into the searchResults target. This controller observes that
// container via MutationObserver to filter out already-added locations.
export default class extends Controller {
  static targets = ["list", "hiddenInputs", "searchInput", "searchResults", "emptyState"]
  static values = { formId: String, inputPrefix: String }

  connect() {
    this._observer = null
  }

  searchResultsTargetConnected(target) {
    this._observer = new MutationObserver(() => this._filterResults())
    this._observer.observe(target, { childList: true })
  }

  searchResultsTargetDisconnected() {
    if (this._observer) {
      this._observer.disconnect()
      this._observer = null
    }
  }

  add(event) {
    event.preventDefault()
    const el = event.currentTarget
    const id = el.dataset.locationId
    const name = el.dataset.locationName
    const state = el.dataset.locationState

    // Prevent duplicates
    if (this.listTarget.querySelector(`[data-location-id="${id}"]`)) return

    this._appendItem(id, name, state)
    this._rebuildHiddenInputs()
    this._updateEmptyState()

    // Remove the clicked result from the dropdown
    el.remove()
  }

  remove(event) {
    event.preventDefault()
    const item = event.currentTarget.closest("[data-location-id]")
    if (item) {
      item.remove()
      this._rebuildHiddenInputs()
      this._updateEmptyState()
    }
  }

  moveUp(event) {
    event.preventDefault()
    const item = event.currentTarget.closest("[data-location-id]")
    const prev = item?.previousElementSibling
    if (prev) {
      item.parentNode.insertBefore(item, prev)
      this._rebuildHiddenInputs()
    }
  }

  moveDown(event) {
    event.preventDefault()
    const item = event.currentTarget.closest("[data-location-id]")
    const next = item?.nextElementSibling
    if (next) {
      next.parentNode.insertBefore(next, item)
      this._rebuildHiddenInputs()
    }
  }

  _filterResults() {
    if (!this.hasSearchResultsTarget) return

    const existingIds = new Set(
      Array.from(this.listTarget.querySelectorAll("[data-location-id]"))
           .map(el => el.dataset.locationId)
    )

    let anyVisible = false
    this.searchResultsTarget.querySelectorAll("[data-location-id]").forEach(el => {
      if (existingIds.has(el.dataset.locationId)) {
        el.remove()
      } else {
        anyVisible = true
      }
    })

    // If all results were filtered out, show a message
    if (!anyVisible && this.searchResultsTarget.querySelectorAll("[data-location-id]").length === 0 &&
        this.searchResultsTarget.children.length === 0 &&
        this.searchInputTarget.value.trim().length >= 2) {
      this.searchResultsTarget.innerHTML = '<div class="px-3 py-2 text-xs text-support-dark-500">All results already added</div>'
    }
  }

  _appendItem(id, name, state) {
    const idx = this.listTarget.children.length + 1
    const stateHtml = state ? `<span class="text-[10.5px] text-support-dark-500">${this._escapeHtml(state)}</span>` : ""
    const html = `
      <div data-location-id="${id}" class="flex items-center gap-2 px-2.5 py-2 bg-support-light-200 border border-support-light-300 rounded-lg">
        <span class="text-[10.5px] font-bold text-support-dark-500 w-4 text-right alternate-locations-index">${idx}.</span>
        <div class="flex-1 min-w-0">
          <div class="text-[12.5px] font-semibold text-support-dark-800 truncate">${this._escapeHtml(name)}</div>
          ${stateHtml}
        </div>
        <div class="flex gap-0.5 flex-shrink-0">
          <div class="w-5 h-5 inline-flex items-center justify-center rounded text-support-dark-500 hover:bg-support-light-300 hover:text-support-dark-700 cursor-pointer" data-action="click->alternate-locations#moveUp" title="Move up"><svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 256 256" fill="currentColor"><path d="M213.66,165.66a8,8,0,0,1-11.32,0L128,91.31,53.66,165.66a8,8,0,0,1-11.32-11.32l80-80a8,8,0,0,1,11.32,0l80,80A8,8,0,0,1,213.66,165.66Z"/></svg></div>
          <div class="w-5 h-5 inline-flex items-center justify-center rounded text-support-dark-500 hover:bg-support-light-300 hover:text-support-dark-700 cursor-pointer" data-action="click->alternate-locations#moveDown" title="Move down"><svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 256 256" fill="currentColor"><path d="M213.66,101.66l-80,80a8,8,0,0,1-11.32,0l-80-80A8,8,0,0,1,53.66,90.34L128,164.69l74.34-74.35a8,8,0,0,1,11.32,11.32Z"/></svg></div>
          <div class="w-5 h-5 inline-flex items-center justify-center rounded text-support-dark-500 hover:bg-alert-red-200 hover:text-alert-red-600 cursor-pointer" data-action="click->alternate-locations#remove" title="Remove">✕</div>
        </div>
      </div>
    `
    this.listTarget.insertAdjacentHTML("beforeend", html)
  }

  _rebuildHiddenInputs() {
    const container = this.hiddenInputsTarget
    container.innerHTML = ""
    const items = this.listTarget.querySelectorAll("[data-location-id]")
    const formId = this.formIdValue

    items.forEach((item, idx) => {
      // Update index label
      const indexEl = item.querySelector(".alternate-locations-index")
      if (indexEl) indexEl.textContent = `${idx + 1}.`

      const input = document.createElement("input")
      input.type = "hidden"
      input.name = this.hasInputPrefixValue && this.inputPrefixValue
        ? this.inputPrefixValue
        : "partner_provider_setting[alternate_locations][]"
      input.value = item.dataset.locationId
      if (formId) input.setAttribute("form", formId)
      container.appendChild(input)
    })
  }

  _clearSearch() {
    this.searchInputTarget.value = ""
    this.searchResultsTarget.innerHTML = ""
  }

  _updateEmptyState() {
    if (!this.hasEmptyStateTarget) return
    const hasItems = this.listTarget.children.length > 0
    this.emptyStateTarget.classList.toggle("hidden", hasItems)
  }

  _escapeHtml(str) {
    const div = document.createElement("div")
    div.textContent = str
    return div.innerHTML
  }
}
